import { Trans } from '@lingui/react/macro';
import { type ReactNode, useMemo, useState } from 'react';

import { type OptionType } from '@/api/common';
import { Button } from '@/components/buttons';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/Dialog';
import { SortableList } from '@/components/list';
import { useTableState } from '@/providers/TableState';

type Props = {
  open?: boolean;
  onClose: () => void;
};

type MetricOptionType = OptionType & {
  infoPopover?: ReactNode;
};

const DataTableMetricSelector = ({ open = false, onClose }: Props) => {
  const { columns, columnsMap, columnsState, onColumnsStateChange } = useTableState();
  const [selectedKeys, setSelectedKeys] = useState<string[] | undefined>(() =>
    Object.keys(columnsMap).filter((key) => !(columnsState.hiddenColumns ?? []).includes(key)),
  );

  const options = useMemo<MetricOptionType[]>(
    () =>
      columns
        .filter((column) => column.metricSelector !== false)
        .map((column) => ({
          id: column.id,
          name:
            (typeof column.metricSelector === 'object' ? column.metricSelector.title : null) ??
            column.title ??
            column.id,
          infoPopover: column.infoPopover,
        })),
    [columns],
  );

  const [sortedOptions, setSortedOptions] = useState<MetricOptionType[] | undefined>(options);

  const handleOk = () => {
    onColumnsStateChange({
      sortedColumns: (sortedOptions ?? []).map((option) => option.id),
      hiddenColumns: Object.keys(columnsMap).filter((key) => !selectedKeys?.includes(key)),
    });
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className="md:max-w-lg">
        <DialogHeader>
          <DialogTitle>
            <Trans>Metrics Selection</Trans>
          </DialogTitle>
        </DialogHeader>
        <div className="max-h-125 overflow-y-auto border border-gray-600 p-2">
          <SortableList<MetricOptionType>
            selectionMode="multiple"
            value={selectedKeys}
            options={sortedOptions}
            renderItem={(item) => {
              return (
                <SortableList.Item
                  key={item.id}
                  id={item.id}
                  infoPopover={
                    item.infoPopover ? { title: item.name, body: item.infoPopover } : undefined
                  }
                >
                  {item.name}
                </SortableList.Item>
              );
            }}
            onOptionsChange={setSortedOptions}
            onChange={setSelectedKeys}
          />
        </div>
        <DialogFooter>
          <DialogClose
            render={
              <Button size="large">
                <Trans>Cancel</Trans>
              </Button>
            }
          />
          <Button size="large" variant="on-primary" onClick={handleOk}>
            <Trans>Update Metrics</Trans>
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default DataTableMetricSelector;
